<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export const NOTICE_TIMEOUT_MS = 15000;
const ANCHOR_SELECTOR = '.conversation-details-wrap';

export default {
  name: 'VoiceAnsweredNotice',
  data() {
    return {
      notice: null,
      anchorLeft: null,
    };
  },
  computed: {
    ...mapGetters({ accountId: 'getCurrentAccountId' }),
    titleKey() {
      return this.notice?.direction === 'outbound'
        ? 'CONVERSATION.VOICE_CALL.ANSWERED_NOTICE.TITLE.OUTBOUND'
        : 'CONVERSATION.VOICE_CALL.ANSWERED_NOTICE.TITLE.INBOUND';
    },
    noticeStyle() {
      return this.anchorLeft === null ? {} : { left: `${this.anchorLeft}px` };
    },
  },
  watch: {
    accountId() {
      this.dismiss();
    },
  },
  mounted() {
    this.$emitter.on(BUS_EVENTS.VOICE_CALL_ANSWERED, this.onCallAnswered);
    window.addEventListener('resize', this.measureAnchor);
  },
  beforeDestroy() {
    this.$emitter.off(BUS_EVENTS.VOICE_CALL_ANSWERED, this.onCallAnswered);
    window.removeEventListener('resize', this.measureAnchor);
    clearTimeout(this.$_timer);
  },
  methods: {
    measureAnchor() {
      const target = document.querySelector(ANCHOR_SELECTOR);
      const rect = target ? target.getBoundingClientRect() : null;
      this.anchorLeft = rect?.width
        ? Math.round(rect.left + rect.width / 2)
        : null;
    },
    onCallAnswered(event = {}) {
      if (!event.conversationId) return;
      this.measureAnchor();
      this.notice = event;
      clearTimeout(this.$_timer);
      this.$_timer = setTimeout(this.dismiss, NOTICE_TIMEOUT_MS);
    },
    dismiss() {
      clearTimeout(this.$_timer);
      this.notice = null;
    },
  },
};
</script>

<template>
  <transition name="voice-answered-notice">
    <output
      v-if="notice"
      class="voice-answered-notice flex items-start gap-3 p-4 rounded-2xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
      :style="noticeStyle"
      aria-live="polite"
      data-testid="voice-answered-notice"
    >
      <span
        class="flex items-center justify-center shrink-0 w-8 h-8 rounded-full bg-woot-50 dark:bg-woot-900 text-woot-500 dark:text-woot-400"
        aria-hidden="true"
      >
        <fluent-icon icon="call" size="16" />
      </span>
      <div class="flex flex-col items-start gap-1 min-w-0">
        <p
          class="m-0 text-sm font-medium text-slate-800 dark:text-slate-50 truncate max-w-full"
        >
          {{ $t(titleKey) }}
        </p>
        <p
          class="m-0 text-sm text-slate-600 dark:text-slate-300 truncate max-w-full"
        >
          {{ notice.number }}
        </p>
        <router-link
          :to="notice.to"
          class="mt-1 text-sm font-medium text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300"
          data-testid="voice-answered-notice-open"
          @click.native="dismiss"
        >
          {{ $t('CONVERSATION.VOICE_CALL.ANSWERED_NOTICE.ACTION') }}
        </router-link>
      </div>
      <button
        type="button"
        class="shrink-0 p-1 -m-1 rounded-full text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200"
        data-testid="voice-answered-notice-dismiss"
        :aria-label="$t('CONVERSATION.VOICE_CALL.ANSWERED_NOTICE.DISMISS')"
        :title="$t('CONVERSATION.VOICE_CALL.ANSWERED_NOTICE.DISMISS')"
        @click="dismiss"
      >
        <fluent-icon icon="dismiss" size="14" />
      </button>
    </output>
  </transition>
</template>

<style scoped lang="scss">
.voice-answered-notice {
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 45;
  width: 21rem;
  max-width: calc(100vw - 3rem);
}

.voice-answered-notice-enter-active,
.voice-answered-notice-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.voice-answered-notice-enter,
.voice-answered-notice-leave-to {
  opacity: 0;
  transform: translate(-50%, -0.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .voice-answered-notice-enter-active,
  .voice-answered-notice-leave-active {
    transition: none;
  }
}
</style>
